import { closeSharingDialog } from '../actions/internalActions';
import { ItemWithPolicyTip } from '../types/ItemWithPolicyTip';
import { SharingDialogResponse } from '../types/SharingDialogResponse';
import { ISharingLinkV3 as ISharingLink } from '@ms/odsp-datasources/lib/interfaces/sharing/SharingInterfaces';
import { IPeoplePickerProvider } from '@ms/odsp-datasources/lib/PeoplePicker';
import { ISharingProvider } from '@ms/odsp-datasources/lib/Sharing';
import { Share as ShareComponent } from '@ms/odsp-shared-sharing/lib/components/Share/index';
import SharingStore from '@ms/odsp-shared-sharing/lib/store/odb/SharingStore';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import ResponsiveUI from '@ms/odsp-utilities/lib/browser/ResponsiveUI';
import { IPartialTheme } from 'office-ui-fabric-react/lib/Styling';
import { asOwaDateInUserTz } from 'owa-datetime';
import SharingLinkKind from 'owa-service/lib/contract/SharingLinkKind';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import {
    ClientId,
    ICustomLabeledAudienceConfig,
    ISharingContextInformation,
    ISharingInformation,
    ISharingItemInformation,
    Mode,
} from '@ms/odsp-shared-sharing/lib/interfaces/SharingInterfaces';

export interface ShareProps extends React.Props<any> {
    /* Indicates the existing share link id used in the LinkSettings mode */
    shareId?: string;

    /* Override values for the default Fabric theme */
    theme?: IPartialTheme;

    /* Allows adding a custom labeled audience  option in the link settings view. ICustomLabelAudienceConfig object */
    customLabeledAudienceConfig?: ICustomLabeledAudienceConfig;

    /* The onDismiss callback executed when the share component closes. */
    onDismiss?: (response?: SharingDialogResponse) => void;

    /* PeoplePickerProvider instance to make people picker API calls. */
    peoplePickerProvider?: IPeoplePickerProvider;

    /* The parsed response from GetSharingInformation API, provided by the caller. */
    sharingInformation?: ISharingInformation;

    /* SharingProvider instance to make sharing API calls in SharingStore. */
    sharingProvider?: ISharingProvider<ItemWithPolicyTip>;
}

export default class SharingDialog extends React.Component<ShareProps, {}> {
    private onDismiss: () => void;
    private sharingStore: SharingStore<ItemWithPolicyTip>;
    private sharingItemInformation: ISharingItemInformation;
    private peoplePickerProvider: IPeoplePickerProvider;
    private maxHeight: number;
    private platformDetection: PlatformDetection;
    private breakpoint: number;

    public static childContextTypes = {
        peoplePickerProvider: PropTypes.object.isRequired,
        resize: PropTypes.func.isRequired,
        onDismiss: PropTypes.func,
        sharingStore: PropTypes.object.isRequired,
        usesSharingAbilities: PropTypes.bool.isRequired,
        platformDetection: PropTypes.object.isRequired,
        breakpoint: PropTypes.object.isRequired,
        isMacV4ExperimentOn: PropTypes.bool.isRequired,
    };

    public refs: {
        [key: string]: React.ReactInstance;
        share: Element;
    };

    constructor(props: ShareProps) {
        super(props);
        const sharingContextInfoString = 'sharingContextInfo';
        const sharingContextInfo = window[sharingContextInfoString] as ISharingContextInformation;
        this.onDismiss = props.onDismiss;
        this.peoplePickerProvider = props.peoplePickerProvider;
        this.sharingStore = new SharingStore(
            {
                item: null,
                sharingInformation: props.sharingInformation,
                defaultRecipients: null,
                accessChecked: false,
                mode: Mode.linkSettings,
                sharingContextInformation: sharingContextInfo,
            },
            {
                sharingProvider: props.sharingProvider,
                suiteNavProvider: null,
                peoplePickerProvider: props.peoplePickerProvider,
                policyTipProvider: null,
                graphPeoplePickerHelper: null,
            }
        );
        this.platformDetection = new PlatformDetection();
        this.breakpoint =
            ResponsiveUI.DEFAULT_BREAKPOINTS[ResponsiveUI.determineWindowSize(window.innerWidth)];

        this.maxHeight = 550;

        let totalItemCount = 0;
        let itemName = ' ';

        if (sharingContextInfo) {
            itemName = sharingContextInfo.itemName || ' ';
            totalItemCount = sharingContextInfo.itemCount || 0;
        }

        this.sharingItemInformation = {
            name: itemName,
            childCount: totalItemCount,
            hasDlpPolicy: undefined,
        };
    }

    // getChildContext is used by <ShareComponent /> to get its context. It is a part of the pattern used by OneDrive, and
    // in order to use ShareComponent we are required to have this, with these exact properties.
    public getChildContext() {
        return {
            peoplePickerProvider: this.peoplePickerProvider,
            resize: () => null,
            onDismiss: this.onDismiss,
            sharingStore: this.sharingStore,
            usesSharingAbilities: false,
            platformDetection: this.platformDetection,
            breakpoint: this.breakpoint,
            isMacV4ExperimentOn: false,
        };
    }

    public render(): JSX.Element {
        const props = this.props;
        return (
            <div ref="share">
                <ShareComponent
                    shareId={props.shareId}
                    theme={props.theme}
                    clientId={ClientId.outlookWeb}
                    scenarioId={''}
                    customLabeledAudienceConfig={props.customLabeledAudienceConfig}
                    showExistingAccessOption={true}
                    hideShareDialogOverflowButton={false}
                    enableShimmer={false}
                    createPeopleSharingLinks={false}
                    maxHeight={this.maxHeight}
                    mode={Mode.linkSettings}
                    messageText={''}
                    sharingItemInformation={this.sharingItemInformation}
                />
            </div>
        );
    }

    componentWillUnmount() {
        this.sharingStore.dispose();
        window.removeEventListener('message', this.onPostMessageReceived, false);
    }

    // Note: With Satchel/mobx we should not be doing this. The correct pattern is to store the sharingLink
    // in sharingDialogStore, and access the store with a selector, outside of this component. We would have a utility
    // That would listen to the post messages, that would fire off an action, and update the store.
    // However, <ShareComponent /> is an external component that is not using Satchel, and is setting the sharingLink.
    // Since <ShareComponent /> is not using Satchel, it does not have actions, so it cannot modify our store.
    // We do all of the logic in the component to work around this issue
    componentDidMount() {
        window.addEventListener('message', this.onPostMessageReceived, false);
    }

    private onPostMessageReceived = (event: any) => {
        if (!event.data) {
            return;
        }

        const messageData = JSON.parse(event.data);
        if (!messageData.name) {
            return;
        }

        switch (messageData.name) {
            case 'share_resize':
                break;
            case 'share_dismiss':
            case 'share_completed':
                closeSharingDialog();
                break;
            case 'share_linkSettingsCompleted':
                this.createSharingDialogResponse();
                break;
            default:
                break;
        }
    };

    private createSharingDialogResponse() {
        const sharingLink: ISharingLink = this.sharingStore.getSharingLinkCreated();
        if (sharingLink) {
            const linkKind = sharingLink.sharingLinkKind as number;

            const sharingDialogResponse: SharingDialogResponse = {
                url: sharingLink.url,
                linkKind: linkKind as SharingLinkKind,
                shareId: sharingLink.shareId,
                expirationDate: asOwaDateInUserTz(sharingLink.expiration) || null,
            };

            this.props.onDismiss(sharingDialogResponse);
        }
    }
}
